import React from "react";
import { Helmet } from "react-helmet-async";
import {
  buildCanonicalUrl,
  siteMetadata,
} from "../config/siteMetadata";

const toAbsoluteUrl = (value: string) =>
  value.startsWith("http") ? value : `${siteMetadata.siteUrl}${value}`;

type SeoProps = {
  /**
   * Page-specific segment used in the global title template.
   * Leave undefined to fall back to the default title.
   */
  title?: string;
  /**
   * Optional description; defaults to the site's global description.
   */
  description?: string;
  /**
   * Router path (e.g. `/shows`). Defaults to the homepage.
   */
  path?: string;
  /**
   * Social preview image. Can be relative or absolute; relative paths are resolved against the site origin.
   */
  image?: string;
  /**
   * Open Graph type. Defaults to `website`.
   */
  type?: "website" | "article" | "music.song" | "music.radio_station" | "profile" | "video.other" | "event";
  /**
   * Mark page as non-indexable (robots noindex, nofollow).
   */
  noindex?: boolean;
  /**
   * ISO string for published timestamp (articles/events).
   */
  publishedTime?: string;
  /**
   * ISO string for last modified timestamp.
   */
  modifiedTime?: string;
  /**
   * Optional meta tag overrides/extensions.
   */
  children?: React.ReactNode;
};

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path = "/",
  image,
  type = "website",
  noindex = false,
  publishedTime,
  modifiedTime,
  children,
}) => {
  const metaTitle = title
    ? siteMetadata.titleTemplate.replace("%s", title)
    : siteMetadata.defaultTitle;
  const metaDescription = description ?? siteMetadata.defaultDescription;
  const canonicalUrl = buildCanonicalUrl(path);
  const socialImage = toAbsoluteUrl(image ?? siteMetadata.defaultSocialImage);

  const isArticle = type === "article";
  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteMetadata.siteName} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:secure_url" content={socialImage} />
      <meta property="og:image:alt" content={`${siteMetadata.siteName} artwork`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={siteMetadata.twitterHandle} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={socialImage} />
      <link rel="canonical" href={canonicalUrl} />
      {!noindex && <meta name="robots" content="index, follow" />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {publishedTime && isArticle && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && isArticle && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {modifiedTime && (
        <meta property="og:updated_time" content={modifiedTime} />
      )}
      {children}
    </Helmet>
  );
};

export default Seo;
